import type { Editor } from '@tiptap/react';
import { getAiToolkit } from '@tiptap-pro/ai-toolkit';

import { setEditorAgentActive } from '@/hooks/llm/editor-suggestion-config';
import { setCachedEditor } from '@/hooks/use-block-editor/editor-cache';
import { getBlockRangeBoundaries, isAiToolkitReady, isEditorAlive } from '@/utils/editor-utils';

import {
  getAgentRunRevertData,
  publishAgentRunEvent,
  setAgentRunEditTarget,
  setAgentRunRevertData,
  setAgentRunStartedFromEmptyEditor,
  setAgentRunStreamingEnabled,
} from './agent-run-store';
import { beginStreamingSession } from '../streaming/streaming-preview';
import { createDocumentEditTarget, getTargetedEditRange, type AgentEditTarget } from '../edit-target';
import type { RevertData, RevertTarget } from './agent-run-model';

export type PreparedAgentAttempt = {
  editor: Editor;
  toolkit: ReturnType<typeof getAiToolkit>;
};

// Synchronous attempt setup: prepare the editor run, mark the run started, and seed the
// targeted active selection. Returns the prepared editor + toolkit for the caller to send with.
export function startPreparedAttempt({
  requestId,
  runId,
  editor,
  editTarget,
  streamingEnabled,
}: {
  requestId: string;
  runId: number;
  editor: Editor;
  editTarget: AgentEditTarget;
  streamingEnabled: boolean;
}): PreparedAgentAttempt {
  const preparedEditor = prepareEditorRun({
    requestId,
    runId,
    editor,
    editTarget,
    streamingEnabled,
  });

  publishAgentRunEvent(requestId, runId, { type: 'request_started' });

  const toolkit = getAiToolkit(preparedEditor);
  const targetedRange = getTargetedEditRange(editTarget);
  if (targetedRange) {
    toolkit.setActiveSelection({ from: targetedRange.from, to: targetedRange.to });
  }

  return { editor: preparedEditor, toolkit };
}

export function prepareEditorRun({
  requestId,
  runId,
  editor,
  editTarget,
  streamingEnabled = true,
}: {
  requestId: string;
  runId: number;
  editor: Editor;
  editTarget?: AgentEditTarget;
  streamingEnabled?: boolean;
}): Editor {
  const resolvedEditTarget = editTarget ?? createDocumentEditTarget();
  beginStreamingSession(requestId, runId);
  setEditorAgentActive(editor, true);
  setCachedEditor(requestId, { editor, documentId: requestId, llmRequestId: requestId });
  setAgentRunEditTarget(requestId, resolvedEditTarget);
  const restoreTarget = getRestoreTarget(editor, resolvedEditTarget);
  setAgentRunRevertData(requestId, { beforeDoc: editor.state.doc, restoreTarget });
  setAgentRunStartedFromEmptyEditor(requestId, editor.isEmpty);
  setAgentRunStreamingEnabled(requestId, streamingEnabled);
  return editor;
}

function getRestoreTarget(editor: Editor, editTarget: AgentEditTarget): RevertTarget {
  const targetedRange = getTargetedEditRange(editTarget);
  if (!targetedRange) {
    return { kind: 'document' };
  }

  const { safeFrom, safeTo } = getBlockRangeBoundaries(editor, targetedRange);
  return { kind: 'range', from: safeFrom, to: safeTo };
}

export function revertEditorToOriginalContent(
  requestId: string,
  editor: Editor,
  revertData: RevertData | null = getAgentRunRevertData(requestId)
): void {
  if (!isAiToolkitReady(editor)) return;

  const toolkit = getAiToolkit(editor);
  toolkit.setActiveSelection(null);

  if (!revertData?.beforeDoc) return;

  const applyRevert = () => {
    if (!editor.commands?.revertToOriginalContent) return;

    switch (revertData.restoreTarget.kind) {
      case 'document':
        editor.commands.revertToOriginalContent(revertData.beforeDoc, {
          from: 0,
          to: revertData.beforeDoc.content.size,
        });
        return;
      case 'range':
        editor.commands.revertToOriginalContent(revertData.beforeDoc, {
          from: revertData.restoreTarget.from,
          to: revertData.restoreTarget.to,
        });
        return;
    }
  };

  if (!editor.state.doc.eq(revertData.beforeDoc)) {
    applyRevert();
  }

  requestAnimationFrame(() => {
    if (!isEditorAlive(editor) || editor.state.doc.eq(revertData.beforeDoc)) return;
    applyRevert();
  });
}
